import { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import Layout from "../components/Layout";
import api from "../lib/api";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Separator } from "../components/ui/separator";
import { Progress } from "../components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Calendar, Clock, CurrencyDollar, UsersThree, Books, CheckCircle, Warning, Bank, ChartLineUp, Copy, GearSix, PencilSimple, Trash, Lock } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function ProgrammeDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [programme, setProgramme] = useState(null);
  const [enrollment, setEnrollment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [enrolling, setEnrolling] = useState(false);
  const [bankAccount, setBankAccount] = useState(null);
  const [enrollees, setEnrollees] = useState(null);
  const [activeTab, setActiveTab] = useState("overview");
  const [exchangeRate, setExchangeRate] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [editSaving, setEditSaving] = useState(false);
  const [courseLocks, setCourseLocks] = useState({}); // { course_id: { locked, lock_reason, prev_course_complete } }
  const isAdmin = ["super_admin", "admin"].includes(user?.role);
  const isInstructor = user?.role === "instructor";
  const canViewEnrollees = isAdmin || isInstructor;
  const isStudent = user?.role === "student";

  useEffect(() => {
    api.get(`/programmes/${id}`).then(r => { setProgramme(r.data); setLoading(false); }).catch(() => setLoading(false));
    api.get("/settings/bank-account").then(r => setBankAccount(r.data)).catch(() => {});
    api.get("/exchange-rate").then(r => setExchangeRate(r.data?.usd_to_ngn)).catch(() => {});
    if (isStudent) {
      api.get("/enrollments/my").then(r => {
        const e = r.data.find(en => en.programme_id === id);
        if (e) setEnrollment(e);
      }).catch(() => {});
      // Sequential course lock map.
      api.get(`/access/programme/${id}/courses`).then(r => {
        const map = {};
        (r.data || []).forEach(c => { map[c.id] = { locked: c.locked, lock_reason: c.lock_reason, prev_course_complete: c.prev_course_complete, release_at: c.release_at, release_kind: c.release_kind }; });
        setCourseLocks(map);
      }).catch(() => {});
    }
    if (canViewEnrollees) {
      api.get(`/programmes/${id}/enrollees`).then(r => setEnrollees(r.data)).catch(() => {});
    }
  }, [id, isStudent, canViewEnrollees]);

  // Section L — when the student returns to /programmes/{id} after opening
  // a course, scroll the last-visited course card back into view so they
  // resume in context (not at the top of the free-course block).
  useEffect(() => {
    try {
      if (typeof window === "undefined") return;
      const map = JSON.parse(localStorage.getItem("sbl_last_course_per_programme") || "{}");
      const lastCourseId = map[id];
      if (!lastCourseId) return;
      const scrollOnce = () => {
        const el = document.querySelector(`[data-testid='programme-course-card-${lastCourseId}'], [data-testid^='programme-course-']`);
        if (el) {
          // Prefer exact match by course id; if not present yet, retry.
          const exact = document.querySelector(`[data-testid='programme-course-card-${lastCourseId}']`) || el;
          exact.scrollIntoView({ behavior: "smooth", block: "center" });
          return true;
        }
        return false;
      };
      let tries = 0;
      const t = setInterval(() => { if (scrollOnce() || ++tries > 15) clearInterval(t); }, 200);
      return () => clearInterval(t);
    } catch (_e) { /* localStorage unavailable */ }
  }, [id]);

  const handleEnroll = async () => {
    setEnrolling(true);
    try {
      const { data } = await api.post("/enrollments", { programme_id: id });
      setEnrollment(data);
      toast.success("Successfully enrolled!");
      if (programme.fee <= 0) {
        toast.success("No payment required. You're all set!");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to enroll");
    }
    setEnrolling(false);
  };

  const handleDeleteProgramme = async () => {
    if (!window.confirm("Are you sure you want to delete this programme? This cannot be undone.")) return;
    try {
      await api.delete(`/programmes/${id}`);
      toast.success("Programme deleted");
      navigate("/programmes");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to delete programme"); }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard");
  };

  const openEditDialog = () => {
    setEditForm({
      title: programme.title || "",
      description: programme.description || "",
      duration: programme.duration || "",
      fee: programme.fee || 0,
      min_payment_percent: programme.min_payment_percent || 50,
      start_date: programme.start_date || "",
      end_date: programme.end_date || "",
      cohort: programme.cohort || "",
      status: programme.status || "active",
      thumbnail: programme.thumbnail || "",
    });
    setEditOpen(true);
  };

  const handleEditSave = async () => {
    setEditSaving(true);
    try {
      const { data } = await api.put(`/programmes/${id}`, {
        ...editForm,
        fee: parseFloat(editForm.fee) || 0,
        min_payment_percent: parseFloat(editForm.min_payment_percent) || 50,
      });
      setProgramme({ ...data, courses: programme.courses });
      toast.success("Programme updated successfully");
      setEditOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update programme");
    }
    setEditSaving(false);
  };

  if (loading) {
    return <Layout><div className="p-8"><div className="h-64 bg-slate-100 animate-pulse" /></div></Layout>;
  }
  if (!programme) {
    return <Layout><div className="p-8 text-center"><p className="text-slate-500">Programme not found</p></div></Layout>;
  }

  return (
    <Layout>
      <div className="p-6 md:p-8 lg:p-12" data-testid="programme-detail-page">
        {/* Header */}
        <div className="mb-8">
          <Link to="/programmes" className="text-sm text-blue-800 hover:underline mb-2 inline-block" data-testid="back-to-programmes">
            &larr; Back to Programmes
          </Link>
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Badge variant={programme.status === "active" ? "default" : "secondary"} className="rounded-none text-xs capitalize">{programme.status}</Badge>
                {programme.cohort && <Badge variant="outline" className="rounded-none text-xs">{programme.cohort}</Badge>}
              </div>
              <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-slate-950" style={{ fontFamily: "'Outfit', sans-serif" }}>
                {programme.title}
              </h1>
            </div>
            {isAdmin && (
              <div className="flex gap-2">
                <Button onClick={openEditDialog} variant="outline" className="rounded-none gap-2 text-sm" data-testid="edit-programme-btn">
                  <GearSix size={16} /> Settings
                </Button>
                <Button onClick={handleDeleteProgramme} variant="outline" className="rounded-none gap-2 text-sm text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200" data-testid="delete-programme-btn">
                  <Trash size={16} /> Delete
                </Button>
              </div>
            )}
          </div>
        </div>

        {/* Admin-suspended banner */}
        {isStudent && enrollment?.suspended && (
          <div className="mb-6 border-2 border-red-300 bg-red-50 p-4 flex items-start gap-3" data-testid="programme-suspended-banner">
            <Warning size={20} weight="bold" className="text-red-700 mt-0.5 shrink-0" />
            <div className="text-sm text-red-900">
              <p className="font-bold uppercase tracking-wider text-xs">Programme access suspended by admin</p>
              {enrollment.suspended_reason && (
                <p className="mt-1 text-red-800">{enrollment.suspended_reason}</p>
              )}
              <p className="mt-1 text-red-700/80">You will not be able to open course content until access is restored. Please contact the SBL team.</p>
            </div>
          </div>
        )}

        {/* Tabs for Admin/Instructor */}
        {canViewEnrollees ? (
          <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-6">
            <TabsList className="rounded-none bg-slate-100">
              <TabsTrigger value="overview" className="rounded-none text-sm" data-testid="tab-overview">Overview</TabsTrigger>
              <TabsTrigger value="enrollees" className="rounded-none text-sm" data-testid="tab-enrollees">
                <UsersThree size={14} className="mr-1" /> Enrollees {enrollees ? `(${enrollees.total_enrollees})` : ""}
              </TabsTrigger>
            </TabsList>

            <TabsContent value="overview">
              <ProgrammeOverview programme={programme} enrollment={enrollment} isStudent={isStudent} isAdmin={isAdmin} handleEnroll={handleEnroll} enrolling={enrolling} bankAccount={bankAccount} copyToClipboard={copyToClipboard} exchangeRate={exchangeRate} courseLocks={courseLocks} />
            </TabsContent>

            <TabsContent value="enrollees">
              <EnrolleesView enrollees={enrollees} />
            </TabsContent>
          </Tabs>
        ) : (
          <ProgrammeOverview programme={programme} enrollment={enrollment} isStudent={isStudent} isAdmin={isAdmin} handleEnroll={handleEnroll} enrolling={enrolling} bankAccount={bankAccount} copyToClipboard={copyToClipboard} exchangeRate={exchangeRate} courseLocks={courseLocks} />
        )}

        {/* Edit Programme Dialog */}
        <Dialog open={editOpen} onOpenChange={setEditOpen}>
          <DialogContent className="sm:max-w-lg rounded-none">
            <DialogHeader>
              <DialogTitle className="text-xl font-semibold" style={{ fontFamily: "'Outfit', sans-serif" }}>Programme Settings</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4 max-h-[60vh] overflow-y-auto pr-2">
              <div>
                <Label className="text-sm font-medium text-slate-700">Title</Label>
                <Input value={editForm.title || ""} onChange={e => setEditForm({...editForm, title: e.target.value})} className="mt-1 rounded-none" data-testid="edit-programme-title" />
              </div>
              <div>
                <Label className="text-sm font-medium text-slate-700">Description</Label>
                <Textarea value={editForm.description || ""} onChange={e => setEditForm({...editForm, description: e.target.value})} className="mt-1 rounded-none" rows={3} data-testid="edit-programme-desc" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium text-slate-700">Duration</Label>
                  <Input value={editForm.duration || ""} onChange={e => setEditForm({...editForm, duration: e.target.value})} placeholder="e.g. 6 months" className="mt-1 rounded-none" />
                </div>
                <div>
                  <Label className="text-sm font-medium text-slate-700">Cohort</Label>
                  <Input value={editForm.cohort || ""} onChange={e => setEditForm({...editForm, cohort: e.target.value})} placeholder="e.g. Cohort 2026A" className="mt-1 rounded-none" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium text-slate-700">Fee (USD)</Label>
                  <Input type="number" value={editForm.fee || 0} onChange={e => setEditForm({...editForm, fee: e.target.value})} className="mt-1 rounded-none" />
                </div>
                <div>
                  <Label className="text-sm font-medium text-slate-700">Min Payment %</Label>
                  <Input type="number" value={editForm.min_payment_percent || 50} onChange={e => setEditForm({...editForm, min_payment_percent: e.target.value})} className="mt-1 rounded-none" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium text-slate-700">Start Date</Label>
                  <Input type="date" value={editForm.start_date || ""} onChange={e => setEditForm({...editForm, start_date: e.target.value})} className="mt-1 rounded-none" />
                </div>
                <div>
                  <Label className="text-sm font-medium text-slate-700">End Date</Label>
                  <Input type="date" value={editForm.end_date || ""} onChange={e => setEditForm({...editForm, end_date: e.target.value})} className="mt-1 rounded-none" />
                </div>
              </div>
              <div>
                <Label className="text-sm font-medium text-slate-700">Status</Label>
                <Select value={editForm.status || "active"} onValueChange={v => setEditForm({...editForm, status: v})}>
                  <SelectTrigger className="mt-1 rounded-none" data-testid="edit-programme-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active (Now Enrolling)</SelectItem>
                    <SelectItem value="draft">Draft (Hidden)</SelectItem>
                    <SelectItem value="inactive">Inactive (Not In Session)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button onClick={handleEditSave} disabled={editSaving || !editForm.title} className="w-full bg-blue-800 text-white hover:bg-blue-900 rounded-none" data-testid="save-programme-edit-btn">
                {editSaving ? "Saving..." : "Save Changes"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}

function ProgrammeOverview({ programme, enrollment, isStudent, isAdmin, handleEnroll, enrolling, bankAccount, copyToClipboard, exchangeRate, courseLocks = {} }) {
  const formatNGN = (usd) => {
    if (!exchangeRate || !usd) return "";
    return `NGN ${Math.round(usd * exchangeRate).toLocaleString()}`;
  };
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
      {/* Main Content */}
      <div className="lg:col-span-2 space-y-6">
        <Card className="border border-slate-200 rounded-none shadow-sm">
          <CardContent className="p-6">
            <h3 className="text-lg font-semibold text-slate-900 mb-3" style={{ fontFamily: "'Outfit', sans-serif" }}>About This Programme</h3>
            <p className="text-slate-600 leading-relaxed whitespace-pre-wrap">{programme.description}</p>
          </CardContent>
        </Card>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {programme.duration && (
            <Card className="border border-slate-200 rounded-none shadow-sm">
              <CardContent className="p-4 text-center">
                <Clock size={24} className="text-blue-800 mx-auto mb-2" />
                <p className="text-xs text-slate-500 uppercase tracking-wide">Duration</p>
                <p className="text-sm font-semibold text-slate-900">{programme.duration}</p>
              </CardContent>
            </Card>
          )}
          {programme.start_date && (
            <Card className="border border-slate-200 rounded-none shadow-sm">
              <CardContent className="p-4 text-center">
                <Calendar size={24} className="text-blue-800 mx-auto mb-2" />
                <p className="text-xs text-slate-500 uppercase tracking-wide">Start Date</p>
                <p className="text-sm font-semibold text-slate-900">{programme.start_date}</p>
              </CardContent>
            </Card>
          )}
          <Card className="border border-slate-200 rounded-none shadow-sm">
            <CardContent className="p-4 text-center">
              <CurrencyDollar size={24} className="text-amber-500 mx-auto mb-2" />
              <p className="text-xs text-slate-500 uppercase tracking-wide">Fee</p>
              <p className="text-sm font-semibold text-slate-900">{programme.fee > 0 ? `$${programme.fee.toLocaleString()}` : "Free"}</p>
              {programme.fee > 0 && exchangeRate && (
                <p className="text-xs text-slate-500 mt-0.5">{formatNGN(programme.fee)}</p>
              )}
            </CardContent>
          </Card>
          <Card className="border border-slate-200 rounded-none shadow-sm">
            <CardContent className="p-4 text-center">
              <Books size={24} className="text-emerald-600 mx-auto mb-2" />
              <p className="text-xs text-slate-500 uppercase tracking-wide">Courses</p>
              <p className="text-sm font-semibold text-slate-900">{programme.courses?.length || 0}</p>
            </CardContent>
          </Card>
        </div>

        {/* Courses in Programme */}
        {programme.courses && programme.courses.length > 0 && (
          <div>
            <h3 className="text-lg font-semibold text-slate-900 mb-1" style={{ fontFamily: "'Outfit', sans-serif" }}>Courses</h3>
            {isStudent && <p className="text-xs text-slate-500 mb-4">Complete each course in order — the next course unlocks automatically once the previous one is 100% done.</p>}
            <div className="space-y-3">
              {programme.courses
                .slice()
                .sort((a, b) => (a.sequence_order ?? a.order ?? 9999) - (b.sequence_order ?? b.order ?? 9999) || (a.title || "").localeCompare(b.title || ""))
                .map((course, idx) => {
                  const lockInfo = isStudent ? courseLocks[course.id] : null;
                  const locked = lockInfo?.locked === true;
                  const lockReason = lockInfo?.lock_reason;
                  const releaseAt = lockInfo?.release_at;
                  const releaseAtText = releaseAt ? new Date(releaseAt).toLocaleString() : "";
                  // Visible numbered position so students see the unlock order.
                  const seqNumber = course.sequence_order ?? course.order ?? (idx + 1);
                  const inner = (
                    <Card className={`border rounded-none shadow-sm transition-all ${locked ? "border-slate-200 bg-slate-50 cursor-not-allowed opacity-75" : "border-slate-200 hover:border-slate-400 hover:shadow-md"}`}>
                      <CardContent className="p-4 flex items-center gap-4">
                        <div className={`w-10 h-10 flex items-center justify-center flex-shrink-0 font-bold text-sm ${locked ? "bg-slate-200 text-slate-500" : "bg-blue-800 text-white"}`} data-testid={`programme-course-seq-${course.id}`}>
                          {locked ? <Lock size={18} weight="fill" /> : `0${seqNumber}`.slice(-2)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className={`font-medium ${locked ? "text-slate-500" : "text-slate-900"}`}>
                            <span className="text-xs uppercase tracking-widest text-slate-400 mr-2">{idx + 1}.</span>{course.title}
                          </p>
                          <p className="text-sm text-slate-500 truncate">{course.description}</p>
                          {locked && (
                            <p className="text-[11px] text-amber-700 mt-1" data-testid={`course-lock-reason-${course.id}`}>
                              {lockReason === "previous_course_incomplete"
                                ? "Finish the previous course to unlock this one."
                                : lockReason === "course_locked_by_schedule"
                                ? (releaseAtText ? `Unlocks on ${releaseAtText}.` : "Unlocks on a scheduled date.")
                                : lockReason === "balance_overdue"
                                ? "Settle your outstanding balance to unlock."
                                : lockReason === "insufficient_initial_payment"
                                ? "Pay the 60% upfront to unlock."
                                : lockReason === "waiting_for_start"
                                ? "Unlocks when the programme starts."
                                : "Locked"}
                            </p>
                          )}
                        </div>
                        {locked
                          ? <Badge className="rounded-none text-xs bg-slate-300 text-slate-700">Locked</Badge>
                          : <Badge variant={course.status === "published" ? "default" : "secondary"} className="rounded-none text-xs capitalize">{course.status}</Badge>}
                      </CardContent>
                    </Card>
                  );
                  return locked ? (
                    <div key={course.id} data-testid={`programme-course-card-${course.id}`}>
                      <div data-testid={`programme-course-${idx}-locked`}>{inner}</div>
                    </div>
                  ) : (
                    <Link key={course.id} to={`/courses/${course.id}`} data-testid={`programme-course-card-${course.id}`}>
                      <div data-testid={`programme-course-${idx}`}>{inner}</div>
                    </Link>
                  );
                })}
            </div>
          </div>
        )}
      </div>

      {/* Sidebar */}
      <div className="space-y-4">
        {isStudent && (
          <Card className="border border-slate-200 rounded-none shadow-sm">
            <CardContent className="p-6">
              {!enrollment ? (
                <div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-3" style={{ fontFamily: "'Outfit', sans-serif" }}>Enroll Now</h3>
                  <p className="text-sm text-slate-600 mb-4">Join this programme and start your leadership journey.</p>
                  <Button onClick={handleEnroll} disabled={enrolling} className="w-full bg-blue-800 text-white hover:bg-blue-900 rounded-none" data-testid="enroll-btn">
                    {enrolling ? "Enrolling..." : "Enroll in Programme"}
                  </Button>

                  {/* Bank Account Details for new students */}
                  {programme.fee > 0 && bankAccount && (
                    <div className="mt-4">
                      <Separator className="mb-4" />
                      <BankAccountCard bankAccount={bankAccount} copyToClipboard={copyToClipboard} />
                    </div>
                  )}
                </div>
              ) : (
                <div>
                  <div className="flex items-center gap-2 mb-4">
                    <CheckCircle size={20} className="text-emerald-600" weight="fill" />
                    <h3 className="text-lg font-semibold text-slate-900" style={{ fontFamily: "'Outfit', sans-serif" }}>Enrolled</h3>
                  </div>
                  {enrollment.balance_overdue && (
                    <div className="mb-4 p-4 border border-red-300 bg-red-50" data-testid="balance-overdue-banner">
                      <p className="text-sm font-bold text-red-800 flex items-center gap-2"><Warning size={16} /> Access locked — balance overdue</p>
                      <p className="text-xs text-red-700 mt-1">Your 6-week balance grace window expired. Course materials are blocked until ${enrollment.balance?.toFixed(2)} is settled. Use the Notify Transfer flow below to upload your receipt.</p>
                    </div>
                  )}
                  {!enrollment.balance_overdue && enrollment.balance_warning && (
                    <div className="mb-4 p-4 border border-amber-300 bg-amber-50" data-testid="balance-warning-banner">
                      <p className="text-sm font-bold text-amber-900 flex items-center gap-2"><Warning size={16} /> Final balance reminder</p>
                      <p className="text-xs text-amber-800 mt-1">Only {enrollment.balance_days_remaining} day(s) left to clear ${enrollment.balance?.toFixed(2)}. Access will be suspended automatically when the 6-week deadline expires.</p>
                    </div>
                  )}
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between items-center">
                      <span className="text-slate-500">Enrollment ID</span>
                      <div className="flex items-center gap-1">
                        <span className="text-xs font-mono text-blue-800">{enrollment.id?.slice(0, 8)}...</span>
                        <button onClick={() => copyToClipboard(enrollment.id)} className="text-blue-600 hover:text-blue-800 p-0.5" data-testid="copy-enrollment-id">
                          <Copy size={12} />
                        </button>
                      </div>
                    </div>
                  <div className="flex justify-between">
                      <span className="text-slate-500">Status</span>
                      <Badge variant={enrollment.status === "active" ? "default" : "secondary"} className={`rounded-none text-xs capitalize ${enrollment.status === "active" && enrollment.balance <= 0 ? "bg-emerald-600" : ""}`}>
                        {enrollment.status === "active" && enrollment.balance <= 0 ? "Paid" : enrollment.status?.replace("_", " ")}
                      </Badge>
                    </div>
                    <Separator />
                    <div className="flex justify-between">
                      <span className="text-slate-500">Total Fee</span>
                      <div className="text-right">
                        <span className="font-semibold text-slate-900">${enrollment.total_fee?.toLocaleString()}</span>
                        {exchangeRate && <p className="text-[10px] text-slate-400">{formatNGN(enrollment.total_fee)}</p>}
                      </div>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-500">Amount Paid</span>
                      <div className="text-right">
                        <span className="font-semibold text-emerald-600">${enrollment.amount_paid?.toLocaleString()}</span>
                        {exchangeRate && <p className="text-[10px] text-slate-400">{formatNGN(enrollment.amount_paid)}</p>}
                      </div>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-500">Balance</span>
                      <div className="text-right">
                        <span className={`font-semibold ${enrollment.balance > 0 ? "text-amber-600" : "text-emerald-600"}`}>${enrollment.balance?.toLocaleString()}</span>
                        {exchangeRate && enrollment.balance > 0 && <p className="text-[10px] text-slate-400">{formatNGN(enrollment.balance)}</p>}
                      </div>
                    </div>
                  </div>

                  {enrollment.balance > 0 && enrollment.payment_status !== "paid" && (
                    <div className="mt-4 space-y-3">
                      <Separator />
                      {enrollment.status === "pending_payment" && (
                        <div className="flex items-center gap-2 text-amber-600 text-sm mb-2">
                          <Warning size={16} />
                          <span>Payment required to access content</span>
                        </div>
                      )}
                      {/* Bank Account Details */}
                      {bankAccount && <BankAccountCard bankAccount={bankAccount} copyToClipboard={copyToClipboard} />}
                      <div className="flex flex-col gap-2">
                        <Link to="/payments" className="w-full">
                          <Button className="w-full bg-blue-800 text-white hover:bg-blue-900 rounded-none gap-2 text-sm" data-testid="go-to-notify-transfer">
                            <Bank size={16} /> Notify Transfer
                          </Button>
                        </Link>
                      </div>
                      <p className="text-xs text-slate-500 text-center">After bank transfer, click above to notify the admin for verification.</p>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}

function BankAccountCard({ bankAccount, copyToClipboard }) {
  return (
    <div className="bg-blue-50 border border-blue-200 p-4" data-testid="bank-account-details">
      <div className="flex items-center gap-2 mb-3">
        <Bank size={18} className="text-blue-800" weight="fill" />
        <h4 className="text-sm font-semibold text-blue-900">Bank Transfer Details</h4>
      </div>
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <span className="text-xs text-slate-600">Bank Name</span>
          <span className="text-xs font-semibold text-slate-900">{bankAccount.bank_name}</span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-xs text-slate-600">Account Name</span>
          <span className="text-xs font-semibold text-slate-900">{bankAccount.account_name}</span>
        </div>
        <div className="flex items-center justify-between gap-2">
          <span className="text-xs text-slate-600">Account Number</span>
          <div className="flex items-center gap-1">
            <span className="text-xs font-bold text-blue-800 tracking-wider">{bankAccount.account_number}</span>
            <button onClick={() => copyToClipboard(bankAccount.account_number)} className="text-blue-600 hover:text-blue-800 p-0.5" data-testid="copy-account-number">
              <Copy size={12} />
            </button>
          </div>
        </div>
        {bankAccount.sort_code && (
          <div className="flex items-center justify-between">
            <span className="text-xs text-slate-600">Sort Code</span>
            <span className="text-xs font-semibold text-slate-900">{bankAccount.sort_code}</span>
          </div>
        )}
      </div>
      {bankAccount.notes && (
        <p className="text-[11px] text-slate-500 mt-3 pt-2 border-t border-blue-200 leading-relaxed">{bankAccount.notes}</p>
      )}
    </div>
  );
}

function EnrolleesView({ enrollees }) {
  if (!enrollees) {
    return <div className="py-8 text-center"><div className="h-32 bg-slate-100 animate-pulse" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="enrollees-view">
      {/* Summary */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="border border-slate-200 rounded-none shadow-sm">
          <CardContent className="p-4 text-center">
            <UsersThree size={24} className="text-blue-800 mx-auto mb-2" />
            <p className="text-xs text-slate-500 uppercase tracking-wide">Total Enrollees</p>
            <p className="text-xl font-bold text-slate-900">{enrollees.total_enrollees}</p>
          </CardContent>
        </Card>
        <Card className="border border-slate-200 rounded-none shadow-sm">
          <CardContent className="p-4 text-center">
            <ChartLineUp size={24} className="text-emerald-600 mx-auto mb-2" />
            <p className="text-xs text-slate-500 uppercase tracking-wide">Group Avg Progress</p>
            <p className="text-xl font-bold text-slate-900">{enrollees.group_avg_progress}%</p>
          </CardContent>
        </Card>
        <Card className="border border-slate-200 rounded-none shadow-sm">
          <CardContent className="p-4 text-center">
            <Books size={24} className="text-amber-500 mx-auto mb-2" />
            <p className="text-xs text-slate-500 uppercase tracking-wide">Total Lessons</p>
            <p className="text-xl font-bold text-slate-900">{enrollees.total_lessons}</p>
          </CardContent>
        </Card>
      </div>

      {/* Group Progress Bar */}
      <Card className="border border-slate-200 rounded-none shadow-sm">
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-slate-700">Overall Group Progress</span>
            <span className="text-sm font-bold text-slate-900">{enrollees.group_avg_progress}%</span>
          </div>
          <Progress value={enrollees.group_avg_progress} className="h-3" />
        </CardContent>
      </Card>

      {/* Enrollees Table */}
      {enrollees.enrollees.length > 0 ? (
        <Card className="border border-slate-200 rounded-none shadow-sm overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="text-xs uppercase tracking-wider">Student</TableHead>
                <TableHead className="text-xs uppercase tracking-wider">Status</TableHead>
                <TableHead className="text-xs uppercase tracking-wider">Payment</TableHead>
                <TableHead className="text-xs uppercase tracking-wider">Progress</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-right">Lessons</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {enrollees.enrollees.map((e, idx) => (
                <TableRow key={e.user_id} data-testid={`enrollee-row-${idx}`}>
                  <TableCell>
                    <div>
                      <p className="text-sm font-medium text-slate-900">{e.name}</p>
                      <p className="text-xs text-slate-500">{e.email}</p>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge variant={e.status === "active" ? "default" : "secondary"} className="rounded-none text-xs capitalize">
                      {e.status?.replace("_", " ")}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="text-xs">
                      <span className="text-emerald-600 font-semibold">${e.amount_paid?.toLocaleString()}</span>
                      {e.balance > 0 && <span className="text-amber-600 ml-1">(${e.balance} due)</span>}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2 min-w-[120px]">
                      <Progress value={e.progress_percent} className="h-2 flex-1" />
                      <span className="text-xs font-semibold text-slate-700 w-10 text-right">{e.progress_percent}%</span>
                    </div>
                  </TableCell>
                  <TableCell className="text-right text-xs text-slate-600">
                    {e.completed_lessons}/{e.total_lessons}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      ) : (
        <div className="text-center py-12">
          <UsersThree size={40} className="text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500">No enrollees yet</p>
        </div>
      )}
    </div>
  );
}
